/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://statefulcoders.com/product/material-dashboard-react
* Copyright 2025 Stateful Coders (https://https://statefulcoders.com)

Coded by https://statefulcoders.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function ComplexStatisticsCard({ color, title, count, percentage, icon, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={({ breakpoints }) => ({
        cursor: onClick ? "pointer" : "default",
        height: "100%",
        minHeight: "120px",
        "&:hover": onClick ? {
          transform: "translateY(-2px)",
          boxShadow: (theme) => theme.shadows[8],
          transition: "all 0.2s ease-in-out",
        } : {},

        // Mobile optimizations
        [breakpoints.down("sm")]: {
          minHeight: "100px",
        },
      })}
    >
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={1}
        px={2}
        sx={({ breakpoints }) => ({
          [breakpoints.down("sm")]: {
            px: 1.5,
            pt: 0.5,
          },
        })}
      >
        <MDBox
          variant="gradient"
          bgColor={color}
          color={color === "light" ? "dark" : "white"}
          coloredShadow={color}
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
          sx={({ breakpoints }) => ({
            [breakpoints.down("sm")]: {
              width: "3rem",
              height: "3rem",
              mt: -2,
            },
          })}
        >
          <Icon
            fontSize="medium"
            color="inherit"
            sx={({ breakpoints }) => ({
              [breakpoints.down("sm")]: {
                fontSize: "1.2rem",
              },
            })}
          >
            {icon}
          </Icon>
        </MDBox>
        <MDBox
          textAlign="right"
          lineHeight={1.25}
          sx={({ breakpoints }) => ({
            flex: 1,
            ml: 1,
            [breakpoints.down("sm")]: {
              ml: 0.5,
            },
          })}
        >
          <MDTypography
            variant="button"
            fontWeight="light"
            color="text"
            sx={({ breakpoints }) => ({
              [breakpoints.down("sm")]: {
                fontSize: "0.75rem",
              },
            })}
          >
            {title}
          </MDTypography>
          <MDTypography
            variant="h4"
            sx={({ breakpoints }) => ({
              [breakpoints.down("sm")]: {
                fontSize: "1.5rem",
              },
              [breakpoints.down("xs")]: {
                fontSize: "1.25rem",
              },
            })}
          >
            {count}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider />
      <MDBox
        pb={2}
        px={2}
        sx={({ breakpoints }) => ({
          [breakpoints.down("sm")]: {
            px: 1.5,
            pb: 1.5,
          },
        })}
      >
        <MDTypography
          component="p"
          variant="button"
          color="text"
          display="flex"
          flexWrap="wrap"
          sx={({ breakpoints }) => ({
            [breakpoints.down("sm")]: {
              fontSize: "0.7rem",
            },
          })}
        >
          <MDTypography
            component="span"
            variant="button"
            fontWeight="bold"
            color={percentage.color}
            sx={({ breakpoints }) => ({
              [breakpoints.down("sm")]: {
                fontSize: "0.7rem",
              },
            })}
          >
            {percentage.amount}
          </MDTypography>
          &nbsp;{percentage.label}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ComplexStatisticsCard
ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
};

// Typechecking props for the ComplexStatisticsCard
ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func,
};

export default ComplexStatisticsCard;
